(function () {
  'use strict';
  if (customElements.get('tea-tier-buybox')) return;
  customElements.define('tea-tier-buybox', class extends HTMLElement {
    connectedCallback() {
      if (this.ready || !window.TeaTierModel) return;
      this.ready = true;
      this.model = window.TeaTierModel;
      this.state = this.model.initial();
      this.unitPrice = Number(this.dataset.unitPrice);
      try {
        const data = JSON.parse(document.getElementById(this.dataset.planDataId).textContent);
        if (String(data.product_id) === this.dataset.productId && String(data.variant_id) === this.dataset.variantId) this.planData = data;
      } catch (_) { /* Missing or malformed allocations leave purchasing unavailable. */ }
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.dataset.currency || 'USD' });
      this.addEventListener('change', event => {
        if (this.busy) return;
        this.status('');
        const input = event.target;
        if (input.matches('.tea-tiers__modes input')) this.state = this.model.select(this.state, 'mode', input.value);
        if (input.matches('.tea-tiers__options input')) this.state = this.model.select(this.state, 'quantity', input.value);
        if (input.matches('[data-cadence]')) this.state = this.model.select(this.state, 'months', input.value);
        if (input.matches('[data-quantity]')) this.state = this.model.select(this.state, 'quantity', input.value);
        this.render();
      });
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button) return;
        if (this.busy) return;
        if (button.matches('.tea-tiers__buy')) { this.addToCart(); return; }
        if (button.hasAttribute('data-increment') || button.hasAttribute('data-decrement')) {
          this.status('');
          this.state = this.model.select(this.state, 'quantity', this.state.quantity + (button.hasAttribute('data-increment') ? 1 : -1));
          this.render();
        }
      });
      this.render();
    }
    format(cents) { return this.money.format(cents / 100); }
    text(selector, text) { this.querySelector(selector).textContent = text; }
    status(message) {
      const status = this.querySelector('[data-cart-status]');
      status.textContent = message;
      status.hidden = !message;
    }
    async addToCart() {
      const item = this.model.cartItem(this.state, this.planData);
      if (this.busy || !item || !window.TeaCartSession?.acquire()) return;
      this.busy = true;
      this.render();
      this.status('Adding to cart…');
      const request = (path, body) => window.TeaCartSession.request(path, body);
      let added = false, normalized = false;
      try {
        const model = window.TeaCartModel;
        const data = model.productData(await request('products/' + encodeURIComponent(this.dataset.productHandle) + '.js'), item.id);
        if (!data) throw new Error('The product plans could not be verified.');
        const before = await request('cart.js');
        if (this.state.mode === 'subscription') {
          const total = item.quantity + model.group(before, { variant_id: item.id }, data, this.state.months).reduce((sum, line) => sum + line.quantity, 0);
          const plan = model.resolve(data, total, this.state.months);
          if (!plan) throw new Error('This subscription option is unavailable.');
          item.selling_plan = plan.id;
        }
        await request('cart/add.js', { items: [item] });
        added = true;
        // One subscription per cart: the interval chosen here becomes the cart's.
        const finalCart = await model.normalize(await request('cart.js'), data, request,
          { prefer: this.state.mode === 'subscription' ? this.state.months : undefined });
        normalized = true;
        const smartCart = window.Rebuy?.SmartCart;
        if (smartCart && typeof smartCart.setCart === 'function' && typeof smartCart.show === 'function') {
          await window.TeaCartSession.syncRebuy();
          smartCart.show();
          this.status('Added to cart.');
        } else {
          const displayed = await window.TeaCartSession.syncNative(finalCart, this.querySelector('.tea-tiers__buy'));
          this.status(displayed ? 'Added to cart.' : 'Added to cart. Open your cart to review it.');
        }
      } catch (error) {
        if (normalized) console.warn('Tea native cart refresh:', /^NATIVE_CART_[A-Z_]+$/.test(error.code || '') ? error.code : 'NATIVE_CART_REQUEST_OR_RENDER');
        // A write with no clear answer may still land: hold the cart until a refresh.
        if (error.unconfirmed) window.TeaCartSession.hold();
        this.status(error.unconfirmed ? "We couldn't confirm your last cart update. Refresh the page to continue." :
          normalized ? 'Added, but the cart preview could not be refreshed. Open your cart to review it before adding again.' :
          added ? 'Added, but the subscription update could not be completed. Review your cart before adding again.' : 'Could not add to cart. Please try again.');
        try { await window.TeaCartSession.syncRebuy(); window.Rebuy?.SmartCart?.show?.(); } catch (_) { /* Never retry an uncertain add. */ }
      } finally {
        this.busy = false;
        window.TeaCartSession.release();
        this.render();
      }
    }
    render() {
      const { mode, quantity, months } = this.state;
      const subscription = mode === 'subscription';
      const plan = subscription ? this.model.resolvePlan(this.planData?.plans, quantity, months) : null;
      const price = subscription ? (plan ? { total: plan.price * quantity, compare: (plan.compare_at_price ?? this.unitPrice) * quantity } : null) : this.model.price(this.state, this.unitPrice);
      this.querySelectorAll('input, select, button').forEach(el => { el.disabled = !!this.busy; });
      this.querySelector('.tea-tiers__buy').disabled = !!this.busy || !this.model.cartItem(this.state, this.planData);
      this.querySelector('.tea-tiers__buy').setAttribute('aria-busy', String(!!this.busy));
      this.querySelector('[data-subscription]').hidden = !subscription;
      this.querySelectorAll('[data-onetime]').forEach(el => { el.hidden = subscription; });
      this.querySelectorAll('.tea-tiers__modes input').forEach(el => { el.checked = el.value === mode; });
      this.querySelectorAll('.tea-tiers__options input').forEach(el => { el.checked = Number(el.value) === quantity; });
      this.querySelector('[data-quantity]').value = quantity;
      this.querySelector('[data-decrement]').disabled = !!this.busy || quantity <= 1;
      this.querySelectorAll('.tea-tiers__option').forEach(option => {
        const q = Number(option.querySelector('input').value);
        const allocation = this.model.resolvePlan(this.planData?.plans, q, q === quantity && subscription ? months : q);
        const compare = allocation?.compare_at_price ?? this.unitPrice;
        option.querySelector('.tea-tiers__prices strong').textContent = allocation ? this.format(allocation.price * q) : 'Unavailable';
        option.querySelector('.tea-tiers__prices s').textContent = this.format(compare * q);
        option.querySelector('.tea-tiers__prices s').hidden = !allocation || compare <= allocation.price;
        const saving = option.querySelector('.tea-tiers__saving');
        saving.hidden = !allocation || compare <= allocation.price;
        if (allocation && compare > 0) saving.textContent = `Save ${Math.round((1 - allocation.price / compare) * 100)}%`;
      });
      this.querySelectorAll('[data-cadence] option').forEach(option => {
        const n = Number(option.value);
        option.textContent = `Every ${n} ${n === 1 ? 'month' : 'months'}`;
      });
      if (subscription) this.querySelector('[data-cadence]').value = months;
      this.text('[data-total]', price ? this.format(price.total) : 'Unavailable');
      this.text('[data-compare]', price ? this.format(price.compare) : '');
      this.querySelector('[data-compare]').hidden = !subscription || !price || price.compare <= price.total;
      this.text('[data-terms]', subscription ? 'Change, skip or cancel anytime' : 'Free shipping when purchasing 2+ boxes');
      this.text('[data-announcement]', price ? `${quantity} ${quantity === 1 ? 'box' : 'boxes'}, ${this.format(price.total)}${subscription ? ` every ${months} ${months === 1 ? 'month' : 'months'}` : ', ships once'}.` : 'This subscription option is unavailable.');
    }
  });
})();
